import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@theme/useTheme';
import { useAuth } from '@contexts/AuthContext';
import { apiService } from '@services/api';
import AssessmentCard from '@components/assessment/AssessmentCard';
import GradientBackground from '@components/ui/GradientBackground';
import ProfileModal from '@components/modals/ProfileModal';
import { FaHistory, FaChartLine, FaRedo, FaHome, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaChevronDown } from 'react-icons/fa';
import Spinner from '@components/ui/Spinner';
import { trackGtmButtonClick } from '@/analytics/gtm';
import { TrackedLink } from '@components/analytics/TrackedLink';

interface ResultsAssessment {
  risk_score: number;
  risk_level: string;
  risk_factors?: string[];
  recommendations?: string[];
  bmi?: number;
  weight?: number;
  height?: number;
  heightFeet?: number;
  heightInch?: number;
  taken_at: string;
}

// Collapsible Section Component
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  items: string[];
  colors: any;
  badgeColor: string;
}> = React.memo(({ title, icon, count, isExpanded, onToggle, items, colors, badgeColor }) => (
  <div className="space-y-3">
    <button
      onClick={onToggle}
      className="flex items-center justify-between w-full p-3 rounded-lg border transition-all duration-200 hover:opacity-90 cursor-pointer"
      style={{ backgroundColor: colors.input, borderColor: colors.input }}
      aria-expanded={isExpanded}
      aria-controls={`${title}-content`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-semibold" style={{ color: colors.heading }}>{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="text-sm px-2 py-1 rounded-full"
          style={{
            backgroundColor: badgeColor,
            color: colors.background
          }}
        >
          {count}
        </span>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FaChevronDown className="w-4 h-4" style={{ color: colors.text }} />
        </motion.div>
      </div>
    </button>
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          id={`${title}-content`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
            <div className="space-y-2">
              {items.map((item: string, index: number) => (
                <motion.div
                  key={index}
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="flex items-start gap-2"
                >
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: badgeColor }}></div>
                  <span className="text-sm" style={{ color: colors.text }}>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
));

CollapsibleSection.displayName = 'CollapsibleSection';

const Results: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { isAuthenticated, userToken } = useAuth();
  const [assessment, setAssessment] = React.useState<ResultsAssessment | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [bgColor, setBgColor] = React.useState<string>(colors.primaryButton);
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({});
  const [showProfile, setShowProfile] = React.useState(false);

  const formDataFromLocation = location.state?.formData;

  // Memoize risk level calculations
  const riskLevelData = useMemo(() => {
    if (!assessment) return null;

    const getRiskLevelIcon = (riskLevel: string) => {
      switch (riskLevel.toLowerCase()) {
        case 'high':
          return <FaExclamationTriangle className="w-5 h-5" style={{ color: colors.error }} />;
        case 'moderate':
          return <FaInfoCircle className="w-5 h-5" style={{ color: '#f59e0b' }} />;
        case 'low':
          return <FaCheckCircle className="w-5 h-5" style={{ color: '#10b981' }} />;
        default:
          return <FaChartLine className="w-5 h-5" style={{ color: colors.text }} />;
      }
    };

    const getRiskLevelColor = (riskLevel: string) => {
      switch (riskLevel.toLowerCase()) {
        case 'high':
          return {
            text: colors.error,
            background: colors.error + '20',
            border: colors.error + '40'
          };
        case 'moderate':
          return {
            text: '#f59e0b',
            background: '#fef3c7',
            border: '#fde68a'
          };
        case 'low':
          return {
            text: '#10b981',
            background: '#d1fae5',
            border: '#a7f3d0'
          };
        default:
          return {
            text: colors.text,
            background: colors.input,
            border: colors.border
          };
      }
    };

    const getRiskMessage = (riskLevel: string) => {
      switch (riskLevel.toLowerCase()) {
        case 'high':
          return 'Consider consulting with a healthcare provider for further evaluation.';
        case 'moderate':
          return 'Monitor your symptoms and consider lifestyle changes.';
        case 'low':
          return 'Continue maintaining healthy sleep habits.';
        default:
          return '';
      }
    };

    return {
      icon: getRiskLevelIcon(assessment.risk_level),
      colors: getRiskLevelColor(assessment.risk_level),
      message: getRiskMessage(assessment.risk_level)
    };
  }, [assessment, colors]);

  // Format height display
  const formattedHeight = useMemo(() => {
    if (!assessment) return null;
    
    if (assessment.heightFeet !== undefined && assessment.heightInch !== undefined) {
      return `${assessment.heightFeet}'${assessment.heightInch}"`;
    }
    
    return assessment.height ? `${assessment.height} ft` : null;
  }, [assessment]);

  React.useEffect(() => {
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  const handleCloseProfile = useCallback(() => {
    setShowProfile(false);
  }, []);

  React.useEffect(() => {
    const fetchLatestAssessment = async () => {
      try {
        if (formDataFromLocation) {
          setAssessment({
            risk_score: formDataFromLocation.riskScore || 0,
            risk_level: formDataFromLocation.riskLevel || 'Low',
            risk_factors: formDataFromLocation.riskFactors || [],
            recommendations: formDataFromLocation.recommendations || [],
            bmi: formDataFromLocation.bmi,
            weight: formDataFromLocation.weight,
            height: formDataFromLocation.height,
            heightFeet: formDataFromLocation.heightFeet,
            heightInch: formDataFromLocation.heightInch,
            taken_at: new Date().toISOString(),
          });
          setLoading(false);
          return;
        }

        // Otherwise, fetch from API
        if (isAuthenticated && userToken) {
          const response = await apiService.getLatestAssessment();
          if (response.has_assessment && response.assessment) {
            setAssessment(response.assessment);
          } else {
            setAssessment(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch assessment:', error);
        setAssessment(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestAssessment();
  }, [isAuthenticated, userToken, formDataFromLocation]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Navigation handlers
  const handleViewHistory = useCallback(() => navigate('/assessment/history'), [navigate]);
  const handleRetakeAssessment = useCallback(() => navigate('/assessment'), [navigate]);
  const handleGoHome = useCallback(() => navigate('/'), [navigate]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: colors.background }}
      >
        <div className="flex-1 flex items-center justify-center pt-16">
          <div className="text-center">
            <Spinner size="xl" color={colors.primary} className="mb-4" />
            <p style={{ color: colors.text }}>Loading your results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: colors.background }}
      >
        <div className="flex-1 flex items-center justify-center px-4 pt-16">
          <div className="max-w-md w-full text-center">
            <h1
              className="text-3xl font-bold mb-4"
              style={{ color: colors.heading }}
            >
              No Assessment Found
            </h1>
            <p
              className="text-lg mb-8"
              style={{ color: colors.text }}
            >
              You haven't completed any assessments yet. Take our assessment to see your results.
            </p>
            <button
              onClick={() => {
                trackGtmButtonClick({
                  button_id: 'results_page_empty_take_assessment',
                  section: 'page_results',
                  label: 'Take Assessment',
                  destination: '/assessment',
                });
                handleRetakeAssessment();
              }}
              className="px-6 py-3 rounded-xl font-semibold text-white transition-colors duration-200"
              style={{ backgroundColor: colors.primaryButton }}
            >
              Take Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.background }}>
      <div className="flex-1">
        {/* Hero Section */}
        <div className="relative pt-32 pb-8 sm:pt-40 sm:pb-12 lg:pt-48 lg:pb-16 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
          <GradientBackground position="top" height="large" />

          <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
            <div className="max-w-7xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <motion.h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                  style={{ color: colors.heading }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  Your Sleep Apnea Assessment Results
                </motion.h1>
                <motion.p
                  className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                  style={{ color: colors.text }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                >
                  Based on your responses, here's your personalized risk assessment and recommendations
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">

            {/* Assessment Results */}
            <section className="mt-4 mb-8 sm:mb-12">
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Assessment Card - Takes up 2 columns on large screens */}
                <motion.div
                  className="lg:col-span-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <AssessmentCard
                    data={{
                      risk_score: assessment.risk_score,
                      risk_level: assessment.risk_level,
                      taken_at: new Date(assessment.taken_at),
                    }}
                    bgColor={bgColor}
                    setBgColor={setBgColor}
                  />
                </motion.div>

                {/* Action Buttons Sidebar */}
                <motion.div
                  className="lg:col-span-1 space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {/* Quick Actions */}
                  <div
                    className="rounded-tr-3xl rounded-bl-3xl border p-6"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border
                    }}
                  >
                    <h3 className="text-lg font-semibold mb-4" style={{ color: colors.heading }}>
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_page_quick_history',
                            section: 'page_results',
                            label: 'View Previous Results',
                            destination: '/assessment/history',
                          });
                          handleViewHistory();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.primaryButton, color: 'white' }}
                      >
                        <FaHistory className="w-5 h-5" />
                        View Previous Results
                      </button>
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_page_quick_retake',
                            section: 'page_results',
                            label: 'Retake Assessment',
                            destination: '/assessment',
                          });
                          handleRetakeAssessment();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.secondaryButton, color: colors.text }}
                      >
                        <FaRedo className="w-5 h-5" />
                        Retake Assessment
                      </button>
                      <button
                        onClick={() => {
                          trackGtmButtonClick({
                            button_id: 'results_page_quick_home',
                            section: 'page_results',
                            label: 'Back to Home',
                            destination: '/',
                          });
                          handleGoHome();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.input, color: colors.text }}
                      >
                        <FaHome className="w-5 h-5" />
                        Back to Home
                      </button>
                    </div>
                  </div>

                  {/* Risk Level Summary */}
                  {riskLevelData && (
                    <div
                      className="rounded-tr-3xl rounded-bl-3xl border p-6"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border
                      }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        {riskLevelData.icon}
                        <h3 className="text-lg font-semibold" style={{ color: colors.heading }}>
                          Risk Level
                        </h3>
                      </div>
                      <div
                        className="px-4 py-2 rounded-lg border text-center font-semibold"
                        style={{
                          color: riskLevelData.colors.text,
                          backgroundColor: riskLevelData.colors.background,
                          borderColor: riskLevelData.colors.border
                        }}
                      >
                        {assessment.risk_level} Risk
                      </div>
                      <p className="text-sm mt-3" style={{ color: colors.text }}>
                        {riskLevelData.message}
                      </p>
                    </div>
                  )}
                </motion.div>
              </div>
            </section>

            {/* Detailed Results Section */}
            <motion.div
              className="mt-8 lg:mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div
                className="rounded-tr-3xl rounded-bl-3xl border p-6 sm:p-8"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border
                }}
              >
                <h2 className="text-xl sm:text-2xl font-bold mb-6" style={{ color: colors.heading }}>
                  Detailed Assessment Results
                </h2>

                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Risk Factors */}
                  {assessment.risk_factors && assessment.risk_factors.length > 0 && (
                    <CollapsibleSection
                      title="Risk Factors"
                      icon={<FaExclamationTriangle style={{ color: colors.primary }} />}
                      count={assessment.risk_factors.length}
                      isExpanded={expandedSections.riskFactors || false}
                      onToggle={() => toggleSection('riskFactors')}
                      items={assessment.risk_factors}
                      colors={colors}
                      badgeColor={colors.primary}
                    />
                  )}

                  {/* Recommendations */}
                  {assessment.recommendations && assessment.recommendations.length > 0 && (
                    <CollapsibleSection
                      title="Recommendations"
                      icon={<FaCheckCircle style={{ color: colors.primary }} />}
                      count={assessment.recommendations.length}
                      isExpanded={expandedSections.recommendations || false}
                      onToggle={() => toggleSection('recommendations')}
                      items={assessment.recommendations}
                      colors={colors}
                      badgeColor={colors.primaryButton}
                    />
                  )}

                  {/* Key Metrics */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: colors.input }}>
                      <FaChartLine style={{ color: colors.primary }} />
                      <span className="font-semibold" style={{ color: colors.heading }}>Key Metrics</span>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span style={{ color: colors.text }}>Risk Score:</span>
                          <span className="font-semibold" style={{ color: colors.heading }}>{assessment.risk_score}%</span>
                        </div>
                        {assessment.bmi && (
                          <div className="flex justify-between">
                            <span style={{ color: colors.text }}>BMI:</span>
                            <span className="font-semibold" style={{ color: colors.heading }}>{assessment.bmi.toFixed(1)}</span>
                          </div>
                        )}
                        {assessment.weight && (
                          <div className="flex justify-between">
                            <span style={{ color: colors.text }}>Weight:</span>
                            <span className="font-semibold" style={{ color: colors.heading }}>{assessment.weight} lbs</span>
                          </div>
                        )}
                        {formattedHeight && (
                          <div className="flex justify-between">
                            <span style={{ color: colors.text }}>Height:</span>
                            <span className="font-semibold" style={{ color: colors.heading }}>{formattedHeight}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Call to Action */}
            <section className="text-center mb-8 sm:mb-12 mt-16 sm:mt-20">
              <div
                className="p-8 lg:p-12 rounded-tr-3xl rounded-bl-3xl border"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border
                }}
              >
                <h2
                  className="text-3xl sm:text-4xl font-bold mb-6"
                  style={{ color: colors.heading }}
                >
                  Take Control of Your Sleep Health
                </h2>
                <p
                  className="text-lg mb-8 max-w-3xl mx-auto"
                  style={{ color: colors.text }}
                >
                  Sleep apnea affects millions of people globally and is linked to serious health conditions. Stay informed with the latest medical research and take our comprehensive assessment to understand your risk factors and get personalized health recommendations.
                </p>
                <TrackedLink
                  to="/assessment"
                  className="px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                  style={{
                    backgroundColor: colors.primary,
                    color: colors.background
                  }}
                  gtmId="results_page_cta_take_assessment"
                  gtmSection="page_results"
                  gtmLabel="Take Sleep Assessment"
                >
                  Take Sleep Assessment
                </TrackedLink>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Profile Modal */}
      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default Results;
